import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { get } from 'lodash';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { ESnackbar, SnackbarService } from '../../utilities/snackbar.service';
import { ScreenLockTokenService } from '../services/screen-lock-token.service';
import { ScreenLockUnlockPromptService } from '../services/screen-lock-unlock-prompt.service';
import {
  IScreenLockUnlockRequirement,
  SCREEN_LOCK_RETRIED,
  SCREEN_LOCK_TOKEN_HEADER,
  SCREEN_LOCK_UNLOCK_REQUIRED_STATUS,
} from '../services/screen-lock.interface';
import {
  API_CACHE_MODE_HEADER,
  API_TARGET,
  EApiCacheMode,
  SKIP_ERROR_MESSAGE_AND_REDIRECT_HEADER,
} from './api-interceptor.interceptor';

/**
 * Opts a request out of the screen lock entirely: no token header, no PIN prompt on a `423`. For the calls
 * the lock itself makes — asking the operator to unlock the request that verifies the PIN would recurse.
 */
export const SKIP_SCREEN_LOCK = new HttpContextToken<boolean>(() => false);

/** The same rule `ApiInterceptor` uses to tell its own API calls from absolute urls and local assets. */
const isAppApiUrl = (url: string): boolean => !/^(http|https):\/\//.test(url) && !/\/assets\//.test(url);

/**
 * Which requests the lock has anything to do with. Everything else — reads, the other two API targets,
 * absolute third-party urls, local assets, the lock's own calls — leaves the interceptor on the first line.
 *
 * Writes only: reads are never refused by the lock, and a header that changes between calls would fragment
 * the GET cache for no reason.
 */
const isScreenLockRelevant = (req: HttpRequest<unknown>): boolean =>
  req.method !== 'GET' &&
  !req.context.get(SKIP_SCREEN_LOCK) &&
  req.context.get(API_TARGET) === 'digitalBoards' &&
  isAppApiUrl(req.url);

/**
 * Everything the screen lock adds to the HTTP layer: the unlock token on a write, and the PIN prompt plus
 * one replay when the API refuses one. See SCREEN_LOCK_CONTEXT.md.
 *
 * Registered **outside** `ApiInterceptor` (functional interceptors run before the DI ones), which is what
 * keeps the two apart:
 * - a request the lock is not concerned with returns on the first line, untouched;
 * - the replay goes back out through `next`, so it passes through `ApiInterceptor` like any other request
 *   and its own failures get the normal treatment (401 → re-auth, 428 → version prompt, else snackbar).
 *
 * The only thing `ApiInterceptor` knows about the lock is that it stays quiet on a `423` this interceptor is
 * about to answer.
 */
export const screenLockInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!isScreenLockRelevant(req)) {
    return next(req);
  }

  const tokenService: ScreenLockTokenService = inject(ScreenLockTokenService);
  // Resolved on demand: `ScreenLockUnlockPromptService` opens a dialog whose store talks HTTP, and pulling
  // that graph in while the interceptor chain is being built is a cyclic dependency.
  const injector: Injector = inject(Injector);

  return next(withUnlockTokens(req, tokenService)).pipe(
    catchError((error: unknown) => {
      if (
        !(error instanceof HttpErrorResponse) ||
        error.status !== SCREEN_LOCK_UNLOCK_REQUIRED_STATUS ||
        // A caller that asked for no interruption on failure does not want a keypad either.
        req.headers.has(SKIP_ERROR_MESSAGE_AND_REDIRECT_HEADER)
      ) {
        return throwError(() => error);
      }

      return retryAfterUnlock(req, next, error, injector, tokenService);
    }),
  );
};

/** Proof that the PIN of a locked screen was entered, for the write paths that ask for it. */
function withUnlockTokens(req: HttpRequest<unknown>, tokenService: ScreenLockTokenService): HttpRequest<unknown> {
  const tokens: string | null = tokenService.buildHeaderValue();

  return tokens ? req.clone({ headers: req.headers.set(SCREEN_LOCK_TOKEN_HEADER, tokens) }) : req;
}

/**
 * Asks for the PIN and replays the write once it is given.
 *
 * Without the replay the operator would lose the work: the settings dialog closes as soon as Save is
 * pressed, well before the API answers, so a refused save takes the whole form with it.
 *
 * Replayed at most once, and structurally so: the replay goes *downstream* through `next`, so it never
 * re-enters this interceptor. It carries `SCREEN_LOCK_RETRIED` for `ApiInterceptor`, which reports a second
 * `423` as the ordinary failure it is instead of leaving it to a keypad that is not coming.
 */
function retryAfterUnlock(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  error: HttpErrorResponse,
  injector: Injector,
  tokenService: ScreenLockTokenService,
): Observable<HttpEvent<unknown>> {
  const requirement: IScreenLockUnlockRequirement | undefined = get(error, 'error.message.0.contexts') as
    | IScreenLockUnlockRequirement
    | undefined;

  if (!requirement?.entityType || !requirement.lockedEntities?.length) {
    // A 423 the client cannot read means the two sides disagree about the payload — they ship as a paired
    // release, so this is a deployment problem, not a user one. Said out loud because the visible symptom
    // is only a generic error message with no keypad, which looks like the lock silently not working.
    // eslint-disable-next-line no-console
    console.error(
      'Screen lock: a 423 arrived without a readable `contexts.lockedEntities`, so no PIN could be ' +
        'requested. The API and the frontend are out of step — rebuild and restart both.',
      get(error, 'error.message.0.contexts'),
    );
    // `ApiInterceptor` stayed quiet expecting a keypad that cannot be shown, so say *something*.
    injector.get(SnackbarService).open(injector.get(TranslateService).instant('apiError.error'), ESnackbar.error);

    return throwError(() => error);
  }

  return injector
    .get(ScreenLockUnlockPromptService)
    .requestUnlock(requirement)
    .pipe(
      switchMap((isUnlocked: boolean) => {
        if (!isUnlocked) {
          return throwError(() => error);
        }

        return next(
          withUnlockTokens(
            req.clone({
              context: req.context.set(SCREEN_LOCK_RETRIED, true),
              // The replay is a fresh attempt, not a second subscriber to the one that was refused: the
              // in-flight dedup in `ApiInterceptor` keys on method + url + body, which the replay shares.
              headers: req.headers.set(API_CACHE_MODE_HEADER, String(EApiCacheMode.none)),
            }),
            tokenService,
          ),
        );
      }),
    );
}
